import { MarkerType, type Edge } from '@xyflow/react';
import type { EdgeType } from '@/types/value-graph';

// Human-readable labels for edge relationships, shown on the canvas. The stored
// edgeType (the enum value) stays the source of truth and rides in the edge's
// `data.edgeType`; this map is presentation only. Read along the arrow
// (source → target): e.g. an `assumption-node` edge points assumption → idea,
// so it reads "assumption concerns idea".
export const EDGE_TYPE_META: Record<EdgeType, { label: string }> = {
  impedes: { label: 'impedes' },
  mitigates: { label: 'mitigates' },
  enables: { label: 'enables' },
  improves: { label: 'improves' },
  'depends-on': { label: 'depends on' },
  blocks: { label: 'blocks' },
  'assumption-node': { label: 'concerns' },
  evidences: { label: 'evidences' },
  comprises: { label: 'comprises' },
};

export function edgeDisplayName(edgeType: string): string {
  return EDGE_TYPE_META[edgeType as EdgeType]?.label ?? edgeType;
}

// Overlay edges connect the evidence layer (market_signal / assumption) to the
// spine. They're rendered de-emphasised (dashed, muted) so the structural spine
// reads as the primary flow.
const OVERLAY_EDGE_TYPES = new Set<EdgeType>(['evidences', 'assumption-node']);

export function isOverlayEdgeType(edgeType: string): boolean {
  return OVERLAY_EDGE_TYPES.has(edgeType as EdgeType);
}

const SPINE_COLOR = '#6f6657'; // warm slate — the structural backbone
const OVERLAY_COLOR = '#b3aa99'; // muted warm — evidence/assumption overlay

// Shared visual treatment for an edge, by relationship type. Spine edges are
// solid; overlay edges dashed + muted. No animation (marching-ants are noise on
// a dense graph). Used by both persisted edges and derived synthesis edges so
// the canvas has one consistent edge style.
export function edgeVisual(
  edgeType: string,
): Pick<Edge, 'style' | 'markerEnd' | 'animated' | 'sourceHandle' | 'targetHandle'> {
  const overlay = isOverlayEdgeType(edgeType);
  const color = overlay ? OVERLAY_COLOR : SPINE_COLOR;
  // Spine edges read left→right (source right → target left). Overlay edges
  // are vertical drops into their anchor, from whichever band the overlay
  // lives in: market signals sit above the spine (bottom → top), assumptions
  // below it (top → bottom, via the dedicated invisible handles — see
  // BaseNode). Mirrors OVERLAY_SIDE in graph-layout.ts.
  const rising = edgeType === 'assumption-node';
  return {
    animated: false,
    style: { stroke: color, strokeWidth: overlay ? 1.25 : 1.5, ...(overlay ? { strokeDasharray: '4 4' } : {}) },
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color },
    sourceHandle: rising ? 'top-source' : overlay ? 'bottom' : 'right',
    targetHandle: rising ? 'bottom-target' : overlay ? 'top' : 'left',
  };
}
